import { overlayBackground, WIDGET_STYLES } from './styles';
import type { PublicConfig } from './types';

/** Small helper so the renderer reads as markup rather than DOM bookkeeping. */
export const element = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

/** Theme values are CSS custom properties on the host, so they cross into the shadow root. */
export const applyTheme = (host: HTMLElement, config: PublicConfig) => {
  const { styles } = config.settings;
  host.style.setProperty('--bhi-primary', styles.primaryColor);
  host.style.setProperty('--bhi-button', styles.buttonColor);
  host.style.setProperty('--bhi-background', styles.backgroundColor);
  host.style.setProperty('--bhi-text', styles.textColor);
  host.style.setProperty('--bhi-overlay', overlayBackground(styles.overlayColor));
  host.style.setProperty('--bhi-radius', `${styles.borderRadius}px`);
  host.style.setProperty('--bhi-font', `${styles.fontFamily}, Arial, sans-serif`);
  host.style.setProperty('--bhi-brand-align', styles.brandNameAlign);
  host.style.setProperty('--bhi-brand-size', `${styles.brandNameSize}px`);
  host.style.setProperty('--bhi-brand-weight', styles.brandNameBold ? '700' : '400');
  host.style.setProperty('--bhi-brand-style', styles.brandNameItalic ? 'italic' : 'normal');
  host.style.setProperty('--bhi-brand-transform', styles.brandNameUppercase ? 'uppercase' : 'none');
  host.style.setProperty('--bhi-title-align', styles.headerTextAlign);
  host.style.setProperty('--bhi-title-size', `${styles.headerTextSize}px`);
  host.style.setProperty('--bhi-title-weight', styles.headerTextBold ? '700' : '400');
  host.style.setProperty('--bhi-title-style', styles.headerTextItalic ? 'italic' : 'normal');
  host.style.setProperty('--bhi-title-transform', styles.headerTextUppercase ? 'uppercase' : 'none');
};

export const createShadowRoot = (host: HTMLElement) => {
  const shadow = host.shadowRoot || host.attachShadow({ mode: 'open' });
  shadow.replaceChildren();
  const style = document.createElement('style');
  style.textContent = WIDGET_STYLES;
  shadow.appendChild(style);
  return shadow;
};

export const renderSuccess = (body: HTMLElement, message: string) => {
  const status = element('div', 'bhi-status');
  status.setAttribute('role', 'status');
  status.appendChild(element('p', 'bhi-status-message', message));
  body.replaceChildren(status);
};
